import React from "react";
import { X } from "lucide-react";

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0959bb] disabled:opacity-50";
export const btnSecondary =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50";
export const btnDanger =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:opacity-50";
export const inputClass =
  "mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#096dea] focus:outline-none focus:ring-1 focus:ring-[#096dea] disabled:bg-slate-50";
export const card = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm";

export function Field({ label, hint, children }) {
  return (
    <label className="block text-xs font-semibold text-slate-600">
      {label}
      {children}
      {hint && <span className="mt-1 block text-[11px] font-normal text-slate-400">{hint}</span>}
    </label>
  );
}

export function Modal({ open, title, onClose, children, footer, wide = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <button type="button" className="absolute inset-0 bg-slate-950/50" onClick={onClose} aria-label="Close dialog" />
      <div role="dialog" aria-modal="true" aria-label={title} className={`relative z-10 max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white shadow-2xl ${wide ? "max-w-2xl" : "max-w-md"}`}>
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
}

export function Alert({ kind = "error", children }) {
  if (!children) return null;
  const styles = kind === "error" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700";
  return <div role={kind === "error" ? "alert" : "status"} className={`rounded-xl border px-4 py-2.5 text-sm ${styles}`}>{children}</div>;
}

export const STATUS_STYLES = {
  confirmed: "bg-blue-50 text-blue-700 border-blue-100",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-100",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  no_show: "bg-amber-50 text-amber-700 border-amber-100",
};

export function StatusPill({ status }) {
  return (
    <span className={`inline-block rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${STATUS_STYLES[status] || ""}`}>
      {status.replace("_", " ")}
    </span>
  );
}

// "YYYY-MM-DD" for today in the given IANA timezone.
export function todayIn(timeZone) {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

export function addDays(isoDate, days) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function formatTime(iso, timeZone) {
  return new Intl.DateTimeFormat("en-IN", { timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(iso));
}

export function formatDate(isoDate) {
  return new Intl.DateTimeFormat("en-IN", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(
    new Date(`${isoDate}T00:00:00Z`)
  );
}

export function formatDateTime(iso, timeZone) {
  return new Intl.DateTimeFormat("en-IN", { timeZone, dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
}

// Local "YYYY-MM-DD" and "HH:MM" parts of an instant in the given timezone.
export function localParts(iso, timeZone) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date(iso)).map((p) => [p.type, p.value])
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` };
}

export const hhmm = (value) => (value || "").slice(0, 5);
